import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { ButtonComponent, TranslatePipe, translator } from '@softline/ui-core';
import { SelectDsComponent, SelectDsItem } from '@softline/ui-core/select';
import { FilterTemplateService } from '../../../services/filter-template.service';

/**
 * The "Vorlagen" select - the same component in the filter bar (applies to the committed
 * state) and in the sidebar (applies to the draft, `target="draft"`). Its footer hands
 * "Verwalten" to the host through `manage`.
 */
@Component({
  selector: 'soft-page-filter-templates-dropdown',
  imports: [SelectDsComponent, ButtonComponent, TranslatePipe],
  templateUrl: './page-filter-templates-dropdown.component.html',
  styleUrl: './page-filter-templates-dropdown.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.is-full-width]': 'fullWidth()' },
})
export class PageFilterTemplatesDropdownComponent {
  private readonly t = translator();

  readonly templateService = input.required<FilterTemplateService>();
  /** where a picked template is applied: the committed state (bar) or the sidebar's draft */
  readonly target = input<'source' | 'draft'>('source');
  /** stretch to the host's width (sidebar row) instead of sizing to the label */
  readonly fullWidth = input(false, { transform: booleanAttribute });

  /** the host opens its template management ("Verwalten" in the footer) */
  readonly manage = output<void>();

  private readonly select = viewChild(SelectDsComponent);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** panel widths in px, mirroring --ds-spacing-34 (384): the bar caps there, a full-width
      row (sidebar) locks the panel to its own width so it never outgrows the sidebar */
  private readonly hostWidth = signal<number | null>(null);
  readonly panelMaxWidth = computed(() => (this.fullWidth() ? this.hostWidth() ?? 384 : 384));

  readonly title = computed(() => `${this.t('#CORE_FILTER.TEMPLATES.TITLE')()}`);
  readonly activeId = computed(() => this.templateService().displayedActive()?.id ?? null);
  readonly items = computed<SelectDsItem<string>[]>(() =>
    this.templateService().sortedTemplates().map((template) => ({ value: template.id, label: template.name }))
  );

  constructor() {
    effect(() => {
      this.templateService()
        .load()
        .catch((e) => console.error('[PageFilterTemplatesDropdownComponent] load templates failed', e));
    });
  }

  applyTemplate(id: string | null): void {
    const template = this.templateService().sortedTemplates().find((t) => t.id === id);
    if (!template) return;
    if (this.target() === 'draft') this.templateService().applyToDraft(template);
    else this.templateService().applyAndCommit(template);
  }

  onOpened(): void {
    this.hostWidth.set(Math.round(this.host.nativeElement.getBoundingClientRect().width));
  }

  openTemplateManagement(): void {
    this.close();
    this.manage.emit();
  }

  close(): void {
    this.select()?.close();
  }
}
